import {
  CSSProperties,
  ReactNode,
  useState,
  ChangeEvent,
  useEffect,
} from "react";
import { PropsWithDataTest } from "../../../types";
import { Box } from "../Box";
import { ReactComponent as EditIcon } from "../../../assets/icons/edit_icon.svg";
import { Typography, TypographyProps } from "@mui/material";
import { palette } from "../../../styles/theme";
import { Button } from "../Button";
import { useTranslation } from "react-i18next";
import { Select } from "../Select/Select";
import { SelectChangeEvent } from "@mui/material";
import { Input } from "../Input";
import {
  validateAndFormatPrice,
  formatPriceDisplay,
} from "../../../utils/priceValidation";
import { useCurrency } from "../../hooks/useCurrency";

export interface EditableSelectOption {
  label: string;
  value: any;
  withInput?: boolean;
  unitInput?: "money" | string;
  render?: (option: EditableSelectOption) => ReactNode;
}

interface Props extends PropsWithDataTest {
  name: string;
  inputName?: string;
  onConfirm?: (value: string, inputValue?: string) => void;
  autoFocus?: boolean;
  defaultValue?: string;
  defaultInputValue?: string;
  inputStyle?: CSSProperties;
  styles?: CSSProperties;
  maxWidth?: string;
  label: string;
  options: EditableSelectOption[];
  inline?: boolean;
  fullWidth?: boolean;
  boldLabel?: boolean;
  valueVariant?: TypographyProps["variant"];
  valueColor?: TypographyProps["color"];
  readOnly?: boolean;
}

export const EditableSelectInput = ({
  name,
  inputName,
  autoFocus,
  defaultValue,
  defaultInputValue,
  styles,
  maxWidth,
  label,
  options,
  "data-test": dataTest,
  inline = true,
  fullWidth = false,
  boldLabel = false,
  valueVariant = "textRegular",
  valueColor = palette.grey.nearBlack,
  readOnly = false,
  ...props
}: Props) => {
  const [isEditing, setEditing] = useState(false);

  const [selectValue, setSelectValue] = useState(defaultValue);
  const [inputValue, setInputValue] = useState(defaultInputValue);

  const { t } = useTranslation();
  const { NarrowCurrencyFormatter } = useCurrency();

  const selectedOption = options.find((o) => o.value === selectValue);

  useEffect(() => {
    setSelectValue(defaultValue);
    setInputValue(defaultInputValue);
  }, [defaultValue, defaultInputValue]);

  const handleSave = () => {
    let finalInputValue = inputValue || "";

    // Validate and format price input if this is a money unit
    if (selectedOption?.unitInput === "money" && inputValue) {
      const validatedPrice = validateAndFormatPrice(inputValue);
      if (validatedPrice !== null) {
        finalInputValue = validatedPrice.toString();
      }
    }

    props.onConfirm?.(selectValue || "", finalInputValue);
    setEditing(false);
  };

  const handleCancel = () => {
    setSelectValue(defaultValue);
    setInputValue(defaultInputValue);
    setEditing(false);
  };

  const renderDisplayValue = () => {
    if (!selectedOption) return defaultValue;
    let display = selectedOption.label;
    if (selectedOption.withInput && inputValue) {
      if (selectedOption.unitInput === "money") {
        const numericValue = parseFloat(inputValue);
        if (!isNaN(numericValue)) {
          const formattedPrice = formatPriceDisplay(
            numericValue,
            NarrowCurrencyFormatter
          );
          display += ` (${formattedPrice})`;
        } else {
          display += ` ($${inputValue})`;
        }
      } else {
        display += ` (${inputValue})`;
      }
    }
    return display;
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: inline ? "center" : "flex-start",
        flexDirection: inline ? "row" : "column",
        gap: "8px",
        maxHeight: inline ? "34px" : "auto",
      }}
      fullWidth={fullWidth}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
        }}
      >
        <Typography
          variant={boldLabel ? "textRegularBold" : "textRegular"}
          color={boldLabel ? palette.grey.nearBlack : palette.grey.primary}
          whiteSpace="nowrap"
        >
          {label}
          {inline ? ":" : ""}
        </Typography>
        {!inline && !readOnly && (
          <EditIcon
            className="editable-control-icon"
            onClick={() => setEditing(true)}
            style={{ cursor: "pointer" }}
          />
        )}
      </Box>
      {isEditing ? (
        <Box
          sx={{ display: "flex", alignItems: "center", gap: "12px" }}
          fullWidth={fullWidth}
        >
          <Select
            {...props}
            size="small"
            autoFocus={autoFocus}
            style={{ ...styles, maxWidth }}
            data-test={dataTest}
            value={selectValue}
            onChange={(event: SelectChangeEvent<any>) => {
              setSelectValue(event.target.value);
            }}
            options={options}
          />
          {selectedOption?.withInput && (
            <Input
              size="small"
              style={{ ...styles, maxWidth: "100px", minWidth: "64px" }}
              value={inputValue}
              onChange={(event: ChangeEvent<HTMLInputElement>) => {
                setInputValue(event.target.value);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleSave();
                }
                if (e.key === "Escape") {
                  setEditing(false);
                }
              }}
              unit={selectedOption.unitInput === "money" ? "$" : undefined}
            />
          )}
          <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Button
              size="small"
              variant="contained"
              color="primary"
              onClick={handleSave}
            >
              {t("general.save")}
            </Button>
            <Button
              size="small"
              variant="outlined"
              color="primary"
              onClick={handleCancel}
            >
              {t("general.cancel")}
            </Button>
          </Box>
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "4px",
            py: inline ? "8px" : "0px",
          }}
        >
          <Typography variant={valueVariant} color={valueColor}>
            {renderDisplayValue()}
          </Typography>
          {inline && !readOnly && (
            <EditIcon
              className="editable-control-icon"
              onClick={() => setEditing(true)}
              style={{ cursor: "pointer" }}
            />
          )}
        </Box>
      )}
    </Box>
  );
};
